'use client';

import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import Link from 'next/link';
import useSepetStore from '@/store/sepet.store';
import api from '@/lib/axios';
import { Trash2, ShoppingBag, Minus, Plus, ArrowLeft, ShoppingCart, AlertTriangle } from 'lucide-react';
import Button from '@/components/ui/Button';
import UyariModal from '@/components/ui/UyariModal';
import toast from 'react-hot-toast';
import { API_URL } from '@/lib/config';

interface VaryasyonDetay {
  id: number;
  urun_id: number;
  varyasyon_ad: string;
  satis_fiyati: number;
  stok: number;
  urun: {
    id: number;
    urun_ad: string;
    urun_slug?: string;
    urun_kdv: number;
    urun_resimleri: { dosya_yolu: string }[];
  };
}

export default function SepetPage() {
  const router = useRouter();
  const { kalemler, cikar, miktarGuncelle, temizle } = useSepetStore();
  const [varyasyonlar, setVaryasyonlar] = useState<Record<number, VaryasyonDetay>>({});
  const [yukleniyor, setYukleniyor] = useState(true);
  const [silOnay, setSilOnay] = useState<number | null>(null);
  const [temizleOnay, setTemizleOnay] = useState(false);
  const [sifirOnay, setSifirOnay] = useState<number | null>(null);
  //const [sepetToplam, setSepetToplam] = useState({ ara_toplam: 0, kdv_toplam: 0, genel_toplam: 0 });
  const [sepetToplam, setSepetToplam] = useState({ 
    ara_toplam: 0, kdv_toplam: 0, genel_toplam: 0, 
    kalemler: [] as { varyasyon_id: number; kalem_toplam: number; kalem_ara_toplam: number }[]
  });

  useEffect(() => {
    if (kalemler.length === 0) { setYukleniyor(false); return; }
    const varyasyonlariGetir = async () => {
      setYukleniyor(true);
      const yeniVaryasyonlar: Record<number, VaryasyonDetay> = {};
      for (const kalem of kalemler) {
        try {
          const res = await api.get(`/urunler/varyasyon/${kalem.varyasyon_id}`);
          yeniVaryasyonlar[kalem.varyasyon_id] = res.data;
        } catch (err) {}
      }
      setVaryasyonlar(yeniVaryasyonlar);
      setYukleniyor(false);
    };
    varyasyonlariGetir();
  }, [kalemler.length]);

  useEffect(() => {
    if (kalemler.length === 0) return;
    api.post('/siparisler/sepet-hesapla', {
      kalemler: kalemler.map(k => ({ varyasyon_id: k.varyasyon_id, miktar: k.miktar })),
    }).then(res => setSepetToplam(res.data)).catch(() => {});
  }, [kalemler.length]);

  useEffect(() => {
    if (kalemler.length === 0) return;
    api.post('/siparisler/sepet-hesapla', {
      kalemler: kalemler.map(k => ({ varyasyon_id: k.varyasyon_id, miktar: k.miktar })),
    }).then(res => setSepetToplam(res.data)).catch(() => {});
  }, [kalemler.length]);

  const stoksuKalemler = kalemler.filter(k => {
    const v = varyasyonlar[k.varyasyon_id];
    return v && v.stok === 0;
  });

  const stokAsanKalemler = kalemler.filter(k => {
    const v = varyasyonlar[k.varyasyon_id];
    return v && k.miktar > v.stok;
  });

  const stokSorunu = stoksuKalemler.length > 0 || stokAsanKalemler.length > 0;

  const araToplam = sepetToplam.ara_toplam;
  const kdvToplam = sepetToplam.kdv_toplam;
  const genelToplam = sepetToplam.genel_toplam;

  const handleMiktarAzalt = (varyasyon_id: number, mevcutMiktar: number) => {
    if (mevcutMiktar <= 1) {
      setSifirOnay(varyasyon_id);
    } else {
      miktarGuncelle(varyasyon_id, mevcutMiktar - 1);
    }
  };

  const handleMiktarArttir = (varyasyon_id: number, mevcutMiktar: number) => {
    const v = varyasyonlar[varyasyon_id];
    if (v && mevcutMiktar >= v.stok) {
      toast.error(`Stokta sadece ${v.stok} adet var!`, { duration: 2000 });
      return;
    }
    miktarGuncelle(varyasyon_id, mevcutMiktar + 1);
  };

  if (kalemler.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-20 text-center">
        <div className="w-24 h-24 bg-[var(--color-gray-100)] rounded-full flex items-center justify-center mx-auto mb-6">
          <ShoppingBag className="w-12 h-12 text-[var(--color-gray-400)]" />
        </div>
        <h1 className="text-2xl font-bold text-[var(--color-gray-800)] mb-2">Sepetiniz Boş</h1>
        <p className="text-[var(--color-gray-500)] mb-8">Sepetinize ürün ekleyerek alışverişe başlayın</p>
        <Link href="/urunler">
          <Button size="lg"><ShoppingCart className="w-5 h-5" />Alışverişe Başla</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => router.back()} className="p-2 hover:bg-[var(--color-gray-100)] rounded-[var(--radius-lg)] transition">
          <ArrowLeft className="w-5 h-5 text-[var(--color-gray-600)]" />
        </button>
        <h1 className="text-2xl font-bold text-[var(--color-gray-800)]">
          Sepetim <span className="ml-2 text-sm font-normal text-[var(--color-gray-500)]">({kalemler.length} ürün)</span>
        </h1>
      </div>

      {!yukleniyor && stokSorunu && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-red-700">Sepetinizde stok sorunu olan ürün var</p>
            <p className="text-xs text-red-500 mt-0.5">Siparişe devam etmek için stok sorunu olan ürünleri düzeltin.</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-3">
          {kalemler.map((kalem) => {
            const v = varyasyonlar[kalem.varyasyon_id];
            const resim = v?.urun?.urun_resimleri?.[0]?.dosya_yolu;
            const kdvOrani = Number(v?.urun?.urun_kdv ?? 20);
            //const kdvliTutar = v ? Math.ceil(v.satis_fiyati * (1 + kdvOrani / 100)) : 0;
            const kdvliTutar = v ? Math.round(v.satis_fiyati * (1 + kdvOrani / 100) * 100) / 100 : 0;
            const stokBitti = v && v.stok === 0;
            const stokAsildi = v && kalem.miktar > v.stok && v.stok > 0;

            return (
              <div key={kalem.varyasyon_id}
                className={`bg-white rounded-[var(--radius-xl)] shadow-[var(--shadow-sm)] p-4 flex gap-4 transition ${
                  stokBitti ? 'opacity-60 border border-red-200' :
                  stokAsildi ? 'border border-orange-200' :
                  'hover:shadow-[var(--shadow-md)]'
                }`}>
                <Link href={v?.urun?.urun_slug ? `/${v.urun.urun_slug}` : `/urunler/${v?.urun_id || ''}`} className="flex-shrink-0">
                  <div className="w-24 h-24 bg-[var(--color-gray-100)] rounded-[var(--radius-lg)] overflow-hidden relative">
                    {yukleniyor ? (
                      <div className="w-full h-full bg-[var(--color-gray-200)] animate-pulse" />
                    ) : resim ? (
                      <img src={`${API_URL}${resim}`} alt={v?.urun?.urun_ad} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[var(--color-gray-400)] text-xs">Resim Yok</div>
                    )}
                    {stokBitti && (
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                        <span className="text-white text-xs font-bold text-center px-1">Stok Tükendi</span>
                      </div>
                    )}
                  </div>
                </Link>

                <div className="flex-1 min-w-0">
                  {yukleniyor ? (
                    <div className="space-y-2">
                      <div className="bg-[var(--color-gray-200)] animate-pulse rounded h-4 w-3/4" />
                      <div className="bg-[var(--color-gray-200)] animate-pulse rounded h-3 w-1/2" />
                    </div>
                  ) : (
                    <>
                      <h3 className="font-semibold text-[var(--color-gray-800)] truncate">{v?.urun?.urun_ad}</h3>
                      <p className="text-sm text-[var(--color-gray-500)] mb-1">{v?.varyasyon_ad}</p>
                      {stokBitti ? (
                        <p className="text-xs text-red-500 font-medium">⚠ Bu ürün tükendi, sepetten çıkarın</p>
                      ) : stokAsildi ? (
                        <p className="text-xs text-orange-500 font-medium">⚠ Stokta sadece {v.stok} adet var, miktarı azaltın</p>
                      ) : (
                        <>
                          <p className="text-[var(--color-primary)] font-bold text-lg">
                            {kdvliTutar.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺
                            <span className="text-xs font-normal text-[var(--color-gray-400)] ml-1">(KDV dahil)</span>
                          </p>
                          <p className="text-xs text-[var(--color-gray-400)] mt-0.5">
                            Toplam: {(kdvliTutar * kalem.miktar).toLocaleString('tr-TR')} ₺
                          </p>
                        </>
                      )}
                    </>
                  )}
                </div>

                <div className="flex flex-col items-end justify-between flex-shrink-0">
                  <button
                    onClick={() => setSilOnay(kalem.varyasyon_id)}
                    className="p-1.5 text-[var(--color-gray-400)] hover:text-[var(--color-error)] hover:bg-[var(--color-error-light)] rounded-[var(--radius-md)] transition"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                  {!stokBitti && (
                    <div className="flex items-center gap-2 border border-[var(--color-gray-200)] rounded-[var(--radius-lg)] p-1">
                      <button onClick={() => handleMiktarAzalt(kalem.varyasyon_id, kalem.miktar)} className="w-7 h-7 flex items-center justify-center hover:bg-[var(--color-gray-100)] rounded-[var(--radius-md)] transition">
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="w-6 text-center text-sm font-semibold">{kalem.miktar}</span>
                      <button
                        onClick={() => handleMiktarArttir(kalem.varyasyon_id, kalem.miktar)}
                        className="w-7 h-7 flex items-center justify-center hover:bg-[var(--color-gray-100)] rounded-[var(--radius-md)] transition"
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                  {stokBitti && (
                    <button onClick={() => cikar(kalem.varyasyon_id)} className="text-xs text-red-500 hover:underline mt-1">
                      Kaldır
                    </button>
                  )}
                </div>
              </div>
            );
          })}

          <div className="text-right">
            <button onClick={() => setTemizleOnay(true)} className="text-sm text-[var(--color-error)] hover:underline">
              Sepeti Temizle
            </button>
          </div>
        </div>

        <div className="lg:col-span-1">
          <div className="bg-white rounded-[var(--radius-xl)] shadow-[var(--shadow-md)] p-6 sticky top-24">
            <h2 className="text-lg font-bold text-[var(--color-gray-800)] mb-4">Sipariş Özeti</h2>
            <div className="space-y-2 mb-4 max-h-48 overflow-y-auto">
              {kalemler.map((kalem) => {
                const v = varyasyonlar[kalem.varyasyon_id];
                const kdvOrani = Number(v?.urun?.urun_kdv ?? 20);
                //const kdvliTutar = v ? Math.ceil(v.satis_fiyati * (1 + kdvOrani / 100)) : 0;
                const kalemHesap = sepetToplam.kalemler.find(k => k.varyasyon_id === kalem.varyasyon_id);
                const kdvliTutar = kalemHesap ? kalemHesap.kalem_toplam / kalem.miktar : 0;
                const stokBitti = v && v.stok === 0;
                return (
                  <div key={kalem.varyasyon_id} className={`flex justify-between text-sm ${stokBitti ? 'opacity-50' : ''}`}>
                    <span className="text-[var(--color-gray-600)] truncate mr-2">
                      {v?.urun?.urun_ad || '...'} <span className="text-[var(--color-gray-400)]">x{kalem.miktar}</span>
                      {stokBitti && <span className="text-red-400 ml-1">(Tükendi)</span>}
                    </span>
                    <span className="font-medium flex-shrink-0">{(kdvliTutar * kalem.miktar).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺</span>
                  </div>
                );
              })}
            </div>
            <div className="border-t border-[var(--color-gray-200)] pt-4 mb-4 space-y-2">
              <div className="flex justify-between text-sm text-[var(--color-gray-500)]">
                <span>Ara Toplam (KDV hariç)</span>
                <span>{araToplam.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺</span>
              </div>
              <div className="flex justify-between text-sm text-[var(--color-gray-500)]">
                <span>KDV</span>
                <span>{kdvToplam.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺</span>
              </div>
              <div className="flex justify-between font-bold text-lg border-t border-[var(--color-gray-200)] pt-3">
                <span>Toplam</span>
                <span className="text-[var(--color-primary)]">{genelToplam.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺</span>
              </div>
            </div>

            {stokSorunu && !yukleniyor && (
              <p className="text-xs text-red-500 text-center mb-3">
                Stok sorunu olan ürünleri düzeltin
              </p>
            )}

            <Button fullWidth size="lg" onClick={() => router.push('/siparis')} disabled={stokSorunu || yukleniyor}>
              <ShoppingCart className="w-5 h-5" />Siparişi Tamamla
            </Button>
            <Link href="/urunler" className="block text-center mt-3 text-sm text-[var(--color-primary)] hover:underline">
              Alışverişe Devam Et
            </Link>
          </div>
        </div>
      </div>

      <UyariModal
        acik={silOnay !== null}
        baslik="Ürünü Kaldır"
        mesaj="Bu ürünü sepetten kaldırmak istiyor musunuz?"
        onayMetni="Kaldır"
        onOnayla={() => { cikar(silOnay!); setSilOnay(null); toast.success('Ürün sepetten kaldırıldı'); }}
        onIptal={() => setSilOnay(null)}
      />

      <UyariModal
        acik={sifirOnay !== null}
        baslik="Ürünü Kaldır"
        mesaj="Adet 0'a düşecek. Ürün sepetten kaldırılsın mı?"
        onayMetni="Kaldır"
        onOnayla={() => { cikar(sifirOnay!); setSifirOnay(null); toast.success('Ürün sepetten kaldırıldı'); }}
        onIptal={() => setSifirOnay(null)}
        tip="uyari"
      />

      <UyariModal
        acik={temizleOnay}
        baslik="Sepeti Temizle"
        mesaj="Sepetteki tüm ürünler kaldırılacak. Emin misiniz?"
        onayMetni="Temizle"
        onOnayla={() => { temizle(); setTemizleOnay(false); toast.success('Sepet temizlendi'); }}
        onIptal={() => setTemizleOnay(false)}
      />
    </div>
  );
}